import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * CadencEZ-styled markdown renderer for AI-generated text.
 *
 * Finance AI emits headings, bullet/numbered lists, **bold**, GFM **tables**
 * (variance breakdowns, board-pack figures), blockquotes, and inline
 * `[fact:N]` citation markers. This renderer turns all of that into clean,
 * editorial UI — never raw `**`, `###`, or `|---|` pipes — so AI output reads
 * like a finished document, not a markdown source dump.
 *
 * Design:
 *   - remark-gfm enables tables / strikethrough / task lists / autolinks.
 *   - Tables get hairline borders, a header band, and right-aligned tabular
 *     numbers for numeric cells (auto-detected) — the editorial ledger look.
 *   - Headings h1–h4 are sized down to fit inline panels.
 *   - `[fact:N]` markers become compact superscript citation pills, numbered
 *     in order of first appearance (academic-style ¹ ² ³), so provenance shows
 *     without dumping raw fact ids into the prose.
 *   - Images are stripped (a hallucinated URL never triggers a fetch).
 */

const NUMERIC_RE = /^[¥$€£%+\-(]?\s?-?[\d,]+(\.\d+)?\s?[%)]?$/;

/** Rewrite `[fact:123]` → `` `cite:1` `` with a stable 1-based index per id,
 *  so the inline-code renderer can show a clean superscript citation pill. */
function withCitationTokens(src: string): string {
  const order = new Map<string, number>();
  return src.replace(/\[fact:(\d+)\]/g, (_m, id: string) => {
    if (!order.has(id)) order.set(id, order.size + 1);
    return `\`cite:${order.get(id)}\``;
  });
}

function nodeText(c: React.ReactNode): string {
  if (typeof c === 'string' || typeof c === 'number') return String(c);
  if (Array.isArray(c)) return c.map(nodeText).join('');
  return '';
}

export function MarkdownText({ children, className }: { children: string; className?: string }) {
  return (
    <div
      className={`text-sm text-ink-800 leading-relaxed [&>p]:my-1.5 [&>ul]:my-1.5 [&>ol]:my-1.5 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&>:first-child]:mt-0 ${className ?? ''}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children: c }) => <p>{c}</p>,
          h1: ({ children: c }) => <h2 className="text-[15px] font-semibold text-ink-900 mt-4 mb-1.5">{c}</h2>,
          h2: ({ children: c }) => <h3 className="text-[14px] font-semibold text-ink-900 mt-3.5 mb-1.5">{c}</h3>,
          h3: ({ children: c }) => <h4 className="text-[13px] font-semibold text-ink-900 mt-3 mb-1">{c}</h4>,
          h4: ({ children: c }) => <h5 className="text-[12.5px] font-semibold text-ink-800 mt-2.5 mb-1 uppercase tracking-wide">{c}</h5>,
          ul: ({ children: c }) => <ul className="list-disc pl-5 space-y-0.5">{c}</ul>,
          ol: ({ children: c }) => <ol className="list-decimal pl-5 space-y-0.5">{c}</ol>,
          li: ({ children: c }) => <li>{c}</li>,
          strong: ({ children: c }) => <strong className="font-semibold text-ink-900">{c}</strong>,
          em: ({ children: c }) => <em className="italic">{c}</em>,
          hr: () => <hr className="my-3 border-0 border-t border-border" />,
          blockquote: ({ children: c }) => (
            <blockquote className="my-2 border-l-2 border-emerald-300 pl-3 text-ink-600 italic">{c}</blockquote>
          ),
          // GFM tables — the editorial ledger look, with a horizontal scroll
          // guard so a wide table never breaks the surrounding panel on mobile.
          table: ({ children: c }) => (
            <div className="my-2.5 overflow-x-auto rounded-md border border-border">
              <table className="w-full text-[12.5px] border-collapse">{c}</table>
            </div>
          ),
          thead: ({ children: c }) => <thead className="bg-ink-50/70">{c}</thead>,
          tbody: ({ children: c }) => <tbody className="divide-y divide-border">{c}</tbody>,
          tr: ({ children: c }) => <tr>{c}</tr>,
          th: ({ children: c }) => {
            const numeric = NUMERIC_RE.test(nodeText(c).trim());
            return (
              <th className={`px-3 py-1.5 font-medium text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border ${numeric ? 'text-right' : 'text-left'}`}>
                {c}
              </th>
            );
          },
          td: ({ children: c }) => {
            const numeric = NUMERIC_RE.test(nodeText(c).trim());
            return <td className={`px-3 py-1.5 align-top ${numeric ? 'text-right num tabular-nums text-ink-800' : 'text-ink-700'}`}>{c}</td>;
          },
          pre: ({ children: c }) => (
            <pre className="my-2 overflow-x-auto rounded-md bg-ink-50 border border-border p-3 text-[11.5px] font-mono text-ink-800">{c}</pre>
          ),
          code: ({ children: c }) => {
            const text = nodeText(c);
            const cite = /^cite:(\d+)$/.exec(text);
            if (cite) {
              return (
                <sup className="ml-0.5 inline-flex items-center justify-center rounded bg-emerald-50 px-1 text-[9px] font-semibold text-emerald-700 align-super" title="Cited source">
                  {cite[1]}
                </sup>
              );
            }
            return <code className="rounded-sm bg-ink-100 px-1 py-0.5 text-[11px] font-mono text-ink-900">{c}</code>;
          },
          a: ({ href, children: c }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">
              {c}
            </a>
          ),
          img: () => null,
        }}
      >
        {withCitationTokens(children)}
      </ReactMarkdown>
    </div>
  );
}
